import { useState } from 'react'

import { Icon, TextButton, Tooltip, typography } from '@joor/design-system'
import styled from 'styled-components'

import { LGConnectionStatus, LocationType } from '__generated__/atlas-types'

import BasicInfoViewMode from '../BasicInfoViewMode/BasicInfoViewMode'
import LocationWidgetViewMode from '../LocationWidgetViewMode/LocationWidgetViewMode'
import {
  StyledTitle as LocationStyledTitle,
  StyledAddress,
  StyledSection,
  StyledText,
} from '../LocationWidgetViewMode/LocationWidgetViewMode.styles'
import {
  getFormattedLocation,
  getGoogleMapsUrl,
} from '../LocationWidgetViewMode/LocationWidgetViewMode.utils'
import People from '../People/People'
import { SubmissionLink } from '../SubmissionLink/SubmissionLink'
import {
  StyledHeader as StyledLocationHeader, // eslint-disable-next-line max-len
} from 'features/LeadGen/RetailerProfile/RetailerProfileViewMode/components/LocationWidgetViewMode/LocationWidgetViewMode.styles'
import ProfileImagesViewMode from 'features/LeadGen/RetailerProfile/RetailerProfileViewMode/components/ProfileImagesViewMode/ProfileImagesViewMode'
import { ViewModeIds } from 'features/LeadGen/RetailerProfile/retailerProfilePage.ids'
import {
  RetailerProfile,
  RetailerProfileLocation,
} from 'features/LeadGen/RetailerProfile/retailerProfilePage.types'

const StyledRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 10px;
  gap: 32px;
  @media only screen and (min-width: 1024px) {
    flex-direction: row;
    > *:nth-child(2) {
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
`
const MoreLocationsMessage = styled.div`
  font-family: var(--font-family-alpha);
  font-size: var(--size-font-small);
  font-weight: var(--font-weight-medium);
  line-height: 16px;
  letter-spacing: 0.6px;
  color: var(--color-primary-300);
  margin-top: 16px;
`
const EmptyLocation = styled.div`
  width: 100%;
  margin: 50px;
  @media only screen and (min-width: 1200px) {
    margin: 0 32px;
  }
`
const StyledTitle = styled.div`
  ${typography.alpha.primaryActive.headline4}
  line-height: 32px;
  letter-spacing: 1.2px;
  color: var(--color-primary-700);
  border-bottom: 1px solid var(--color-primary-300);
  width: calc(100% - 64px);
  margin: 10px 0 24px 0;
  padding-bottom: 8px;
  width: 100%;
`

const NewSectionHeader = styled.div`
  width: 100%;
  ${typography.alpha.primaryActive.headline4}
  line-height: 32px;
  letter-spacing: 1.2px;
  color: var(--color-primary-700);
  border-bottom: 1px solid var(--color-primary-300);
  padding: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`

const OtherLocationsHeader = styled.div`
  width: 100%;
  ${typography.alpha.primaryActive.headline4}
  line-height: 32px;
  letter-spacing: 1.2px;
  color: var(--color-primary-700);
  border-bottom: 1px solid var(--color-primary-300);
  padding: 0 0 8px 0;
  margin: 10px 0 24px 0;
  display: flex;
  align-items: center;
  justify-content: start;
  gap: 8px;
`

const CollapsedLocationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin: 16px 0;

  @media only screen and (max-width: 1120px) {
    grid-template-columns: 1fr;
  }
`

const CollapsedLocationCard = styled.div`
  border: 1px solid var(--color-primary-300);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`

const ExpandButton = styled(TextButton)`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: var(--size-font-small) !important;

  > span {
    display: flex;
    align-items: center;
    margin-top: 4px;
    gap: 5px;
  }
`

const ExpandedLocationsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 16px;
  width: 100%;
`

const LocationSeparator = styled.div`
  border-top: 1px solid var(--color-primary-200);
  margin: 24px 0;
`

const MainContainer = styled.div`
  display: flex;
  gap: 20px;
  flex-direction: column;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;

  @media only screen and (min-width: 770px) {
    flex-direction: row;
  }
`

const LeftSideWrapper = styled.div`
  padding: 20px;
  margin: 20px 0 20px 20px;
  border: 1px solid var(--color-primary-300);
  border-radius: 10px;
  max-width: 350px;
  background-color: white !important;
  align-self: flex-start;
  height: fit-content;

  @media only screen and (max-width: 769px) {
    margin: 20px;
    max-width: 100%;
    order: 1;
  }
`

const AboutUsWrapper = styled.div`
  flex: 1;
  min-width: 0;
  padding: 20px;
  margin: 20px 20px 20px 0;
  border: 1px solid var(--color-primary-300);
  border-radius: 10px;
  background-color: white !important;
  overflow: hidden;
  align-self: flex-start;
  height: fit-content;
  max-width: 100%;
  box-sizing: border-box;

  @media only screen and (max-width: 769px) {
    margin: 20px;
    order: 2;
    max-width: calc(100% - 40px);
    box-sizing: border-box;
  }
`

const AboutUsContentWrapper = styled.div`
  padding: 0 65px;
  width: 100%;
  box-sizing: border-box;

  @media only screen and (max-width: 769px) {
    padding: 0 20px;
  }
`

const StyledDescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  @media only screen and (min-width: 1200px) {
    max-width: 100%;
    word-break: break-word;
  }
`

const StyledDescription = styled.div`
  color: var(--color-primary-500);
  font-family: var(--font-family-alpha);
  font-size: var(--size-font-medium);
  font-weight: var(--font-weight-regular);
  line-height: var(--line-height-20);
  letter-spacing: var(--letter-spacing-tighter);
  margin-bottom: 40px;
`

type OtherLocationsSectionProps = {
  locations: RetailerProfileLocation[]
}

const OtherLocationsSection = ({ locations }: OtherLocationsSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <>
      <OtherLocationsHeader>
        <span>Other Locations</span>
        <ExpandButton onClick={() => setIsExpanded(!isExpanded)}>
          {isExpanded ? 'Collapse All Locations' : 'Expand All Locations'}
          <Icon
            iconName={isExpanded ? 'chevron-up' : 'chevron-down'}
            iconSize="small"
          />
        </ExpandButton>
      </OtherLocationsHeader>
      {isExpanded ? (
        <ExpandedLocationsWrapper>
          {locations.map((location: RetailerProfileLocation, index) => (
            <div key={location.id} style={{ width: '100%', minWidth: 0 }}>
              <LocationWidgetViewMode location={location} />
              {index < locations.length - 1 && <LocationSeparator />}
            </div>
          ))}
        </ExpandedLocationsWrapper>
      ) : (
        <CollapsedLocationsGrid>
          {locations.map((location: RetailerProfileLocation) => (
            <CollapsedLocationCard key={location.id}>
              {location.name && (
                <StyledSection>
                  <LocationStyledTitle>Location Name</LocationStyledTitle>
                  <StyledText className="notranslate">
                    {location.name}
                  </StyledText>
                  {(location.type || location.yearEstablished) && (
                    <div style={{ display: 'flex', gap: '3px' }}>
                      {location.type && (
                        <StyledText
                          style={{ fontSize: 'var(--size-font-small)' }}
                        >
                          {location.type === LocationType.STORE
                            ? 'Store'
                            : 'Office'}
                          ,
                        </StyledText>
                      )}
                      {location.yearEstablished && (
                        <StyledText
                          className="notranslate"
                          style={{ fontSize: 'var(--size-font-small)' }}
                        >
                          {location.yearEstablished}
                        </StyledText>
                      )}
                    </div>
                  )}
                </StyledSection>
              )}
              <LocationAddress location={location} />
            </CollapsedLocationCard>
          ))}
        </CollapsedLocationsGrid>
      )}
    </>
  )
}

const LocationAddress = ({
  location,
}: {
  location: RetailerProfileLocation
}) => {
  const { formattedAddress, formattedLocationInfo } =
    getFormattedLocation(location)
  const mapsUrl = getGoogleMapsUrl(location)
  const addressContent = (
    <>
      <div>{formattedAddress}</div>
      <div>{formattedLocationInfo}</div>
      <div>{location.zip}</div>
      <div>{location.phone}</div>
    </>
  )
  return (
    <StyledSection>
      <LocationStyledTitle>Address</LocationStyledTitle>
      <StyledAddress
        className="noTranslate"
        data-testid={`location_addres1_address2_${location?.id}`}
        as={mapsUrl ? 'a' : undefined}
        href={mapsUrl ?? undefined}
        target={mapsUrl ? '_blank' : undefined}
        rel={mapsUrl ? 'noopener noreferrer' : undefined}
        style={
          mapsUrl
            ? {
                cursor: 'pointer',
                textDecoration: 'none',
                color: 'var(--color-secondary-800)',
              }
            : undefined
        }
      >
        {addressContent}
      </StyledAddress>
    </StyledSection>
  )
}

type RetailerProfileContentProps = {
  retailer: RetailerProfile
  isRetailerAccount: boolean
  description: string
}

const RetailerProfileContent = ({
  retailer,
  isRetailerAccount,
  description,
}: RetailerProfileContentProps) => {
  const profileImages = (
    retailer?.profileImages?.map((profileImage) => profileImage?.upload?.url) ??
    []
  ).filter((pf) => pf) as string[]

  return (
    <MainContainer>
      <LeftSideWrapper>
        <BasicInfoViewMode
          socialMedia={retailer?.socialMediaAccounts ?? []}
          website={retailer?.websiteUrl ?? ''}
          awards={retailer?.awards}
        />
        <StyledRow>
          <div style={{ width: '100%' }}>
            <NewSectionHeader>
              <span
                id={
                  isRetailerAccount
                    ? ViewModeIds.PeopleWidget.SectionTitle.Retailer
                    : ViewModeIds.PeopleWidget.SectionTitle.Brand
                }
              >
                People
              </span>
              {retailer.people &&
              retailer.people?.length > 0 &&
              retailer.accountSubmission?.messageId ? (
                <Tooltip
                  defaultPlacement="right"
                  content={
                    <SubmissionLink
                      submissionSent={{
                        messageId: retailer.accountSubmission.messageId,
                        date: retailer.accountSubmission.createdAt,
                      }}
                    />
                  }
                >
                  <Icon iconName="information" iconSize="regular" />
                </Tooltip>
              ) : (
                ''
              )}
            </NewSectionHeader>
            <People
              people={retailer.people}
              isRetailerConnected={
                retailer.connectionStatus === LGConnectionStatus.CONNECTED
              }
              isRetailer={!!isRetailerAccount}
              retailerId={retailer.id}
              accountSubmission={retailer.accountSubmission}
            />
          </div>
        </StyledRow>
      </LeftSideWrapper>
      <AboutUsWrapper>
        {(profileImages.length > 0 || description) && (
          <div>
            <StyledTitle>About Us</StyledTitle>
            <AboutUsContentWrapper>
              {profileImages.length > 0 && (
                <StyledRow
                  style={{
                    width: '100%',
                    maxWidth: '100%',
                    marginBottom: '40px',
                  }}
                >
                  <ProfileImagesViewMode imageUrls={profileImages ?? []} />
                </StyledRow>
              )}
              <StyledDescriptionContainer>
                {description && (
                  <StyledDescription className="noTranslate">
                    {description}
                  </StyledDescription>
                )}
              </StyledDescriptionContainer>
            </AboutUsContentWrapper>
          </div>
        )}
        {retailer.primaryLocation && (
          <StyledRow>
            <div style={{ width: '100%' }}>
              <StyledTitle>Primary Location</StyledTitle>
              <LocationWidgetViewMode location={retailer.primaryLocation} />
            </div>
          </StyledRow>
        )}

        {retailer.locations && retailer.locations.length > 0 && (
          <StyledRow>
            <div style={{ width: '100%' }}>
              <OtherLocationsSection locations={retailer.locations} />
            </div>
          </StyledRow>
        )}

        {(!retailer.locations || retailer.locations.length < 1) &&
          !retailer.primaryLocation && (
            <StyledRow>
              <EmptyLocation>
                <StyledLocationHeader>
                  {isRetailerAccount ? 'Other Locations' : 'Locations'}
                </StyledLocationHeader>
                <MoreLocationsMessage>
                  {isRetailerAccount
                    ? 'Edit page to add additional locations'
                    : 'The retailer has not provided any information about locations.'}
                </MoreLocationsMessage>
              </EmptyLocation>
            </StyledRow>
          )}
      </AboutUsWrapper>
    </MainContainer>
  )
}

export default RetailerProfileContent
